import React from "react";
import dayjs from "dayjs";
import { Box, Button, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

interface QuickRangeButtonsProps {
  dateFrom: string | null;
  dateTo: string | null;
  onRangeChange: (dateFrom: string, dateTo: string) => void;
}

const QuickRangeButtons: React.FC<QuickRangeButtonsProps> = ({
  dateFrom,
  dateTo,
  onRangeChange,
}) => {
  const quickRanges = [
    { label: "Last 24 Hours", hours: 24 },
    { label: "Last 2 Days", hours: 48 },
    { label: "Last 7 Days", hours: 168 },
    { label: "Last 30 Days", hours: 720 },
  ];

  const setQuickTimeRange = (hours: number) => {
    const now = dayjs();
    const from = now.subtract(hours, "hours");
    onRangeChange(from.toISOString(), now.toISOString());
  };

  const isQuickRangeActive = (hours: number): boolean => {
    if (!dateFrom || !dateTo) return false;

    const from = dayjs(dateFrom);
    const to = dayjs(dateTo);
    const diffHours = to.diff(from, "hours", true);

    // Allow 5% tolerance for auto-updating dates
    const tolerance = hours * 0.05;
    return Math.abs(diffHours - hours) <= tolerance;
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: "text.primary",
          fontSize: "1rem",
          mb: 2,
        }}
      >
        Date range
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {quickRanges.map((range) => {
          const isActive = isQuickRangeActive(range.hours);
          return (
            <Button
              key={range.label}
              onClick={() => setQuickTimeRange(range.hours)}
              startIcon={<AccessTimeIcon />}
              sx={{
                height: 48,
                px: 3,
                fontWeight: 600,
                borderRadius: 2,
                textTransform: "none",
                fontSize: "0.9rem",
                border: "2px solid",
                borderColor: isActive ? "primary.main" : "grey.300",
                backgroundColor: isActive ? "primary.main" : "white",
                color: isActive ? "white" : "text.primary",
                boxShadow: isActive
                  ? "0 2px 8px rgba(25, 118, 210, 0.3)"
                  : "none",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  backgroundColor: isActive ? "primary.dark" : "grey.50",
                  borderColor: isActive ? "primary.dark" : "grey.400",
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                },
              }}
            >
              {range.label}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

export default QuickRangeButtons;
